import React, { useMemo, useState } from 'react';
import * as d3 from 'd3';
import { useDimensionsRef } from 'hooks/useDimensionsRef/useDimensionsRef';
import { useChartTooltip } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/useChartTooltip';
import {
  useChartColors,
  REPORTS_PAGE_COLORS,
} from 'pages/ReportsPage/components/AnalyticsDashboards/chartColors';
import { useEmployeeEngagementDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/EmployeeEngagementDashboard/store/employeeEngagement.store';
import {
  LAST_APP_OPENED_ALERT_BUCKET,
  LAST_APP_OPENED_ALERT_COLOR,
  Y_AXIS_ROUNDING_INTERVAL,
} from 'pages/ReportsPage/components/AnalyticsDashboards/EmployeeEngagementDashboard/EmployeeEngagementDashboard.constants';
import { EmptyStateContainer } from 'pages/ReportsPage/components/AnalyticsDashboards/EmployeeEngagementDashboard/EmployeeEngagementDashboard.styled';

const CHART_HEIGHT = 340;
const MARGIN = { top: 24, right: 20, bottom: 44, left: 44 };
const BAR_PADDING = 0.35;
const BAR_RX = 3;

export const LastAppOpenedChart: React.FC = () => {
  const data = useEmployeeEngagementDashboardStore((s) => s.lastAppOpened);
  const palette = useChartColors();
  const [containerRef, { width: containerWidth }] = useDimensionsRef<HTMLDivElement>();
  const tooltip = useChartTooltip();
  const [hovered, setHovered] = useState<string | null>(null);

  const defaultColor = palette.chartColors[3];

  const innerWidth = Math.max(containerWidth - MARGIN.left - MARGIN.right, 0);
  const innerHeight = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

  const xScale = useMemo(
    () =>
      d3
        .scaleBand<string>()
        .domain(data?.map((d) => d.bucket) ?? [])
        .range([0, innerWidth])
        .padding(BAR_PADDING),
    [data, innerWidth],
  );

  const yScale = useMemo(() => {
    const rawMaxCount = d3.max(data ?? [], (d) => d.count) || 1;
    const roundedYAxisMax =
      Math.ceil(rawMaxCount / Y_AXIS_ROUNDING_INTERVAL) * Y_AXIS_ROUNDING_INTERVAL;
    return d3.scaleLinear().domain([0, roundedYAxisMax]).range([innerHeight, 0]);
  }, [data, innerHeight]);

  const yTicks = useMemo(() => yScale.ticks(5), [yScale]);

  if (!data) {
    return <EmptyStateContainer>No data available</EmptyStateContainer>;
  }

  return (
    <div ref={containerRef} style={{ width: '100%', minHeight: CHART_HEIGHT }}>
      {containerWidth > 0 && (
        <svg width={containerWidth} height={CHART_HEIGHT} aria-label="Last App Opened bar chart">
          <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
            {yTicks.map((tick) => (
              <g key={tick}>
                <line
                  x1={0}
                  x2={innerWidth}
                  y1={yScale(tick)}
                  y2={yScale(tick)}
                  stroke={REPORTS_PAGE_COLORS.gridLine}
                />
                <text
                  x={-8}
                  y={yScale(tick)}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fontSize="12px"
                  fill={REPORTS_PAGE_COLORS.axisLabel}
                >
                  {tick}
                </text>
              </g>
            ))}
            {data.map((d) => {
              const barX = xScale(d.bucket) || 0;
              const barWidth = xScale.bandwidth();
              const centerX = barX + barWidth / 2;
              const barY = yScale(d.count);
              const barHeight = innerHeight - barY;
              const isAlert = d.bucket === LAST_APP_OPENED_ALERT_BUCKET;
              const barColor = isAlert ? LAST_APP_OPENED_ALERT_COLOR : defaultColor;

              return (
                <g key={d.bucket}>
                  <rect
                    x={barX}
                    y={barY}
                    width={barWidth}
                    height={barHeight}
                    fill={barColor}
                    rx={BAR_RX}
                    role="img"
                    aria-label={`${d.bucket}: ${d.count} team members`}
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                    opacity={hovered === d.bucket ? 0.8 : 1}
                    onMouseEnter={(e) => {
                      setHovered(d.bucket);
                      tooltip.show(e, `<strong>${d.bucket}</strong><br/>${d.count} team members`);
                    }}
                    onMouseMove={(e) => tooltip.move(e)}
                    onMouseLeave={() => {
                      setHovered(null);
                      tooltip.hide();
                    }}
                    onFocus={(e) => {
                      setHovered(d.bucket);
                      tooltip.show(e, `<strong>${d.bucket}</strong><br/>${d.count} team members`);
                    }}
                    onBlur={() => {
                      setHovered(null);
                      tooltip.hide();
                    }}
                  />
                  <text
                    x={centerX}
                    y={innerHeight + 20}
                    textAnchor="middle"
                    fontSize="12px"
                    fill={REPORTS_PAGE_COLORS.axisLabel}
                  >
                    {d.bucket}
                  </text>
                </g>
              );
            })}
          </g>
        </svg>
      )}
    </div>
  );
};
